/**
 * MobileStundenkonto — READ-ONLY Übersicht.
 * Heute + Woche + Monat + Projektaufschlüsselung.
 *
 * Eingabe/Änderung passiert auf /app/m/eingabe (DayForm).
 */
import { useState, useEffect, useCallback } from "react";
import { authFetch, useAuth } from "../../App";
import DayEntriesSheet from "./DayEntriesSheet";
import { addDays, subDays, startOfWeek, format, parseISO, getISOWeek, addMonths, subMonths } from "date-fns";
import { de, enUS } from "date-fns/locale";
// SK fällt auf DE zurück (kein eigenes SK-Bundle in der date-fns Installation)
const DATE_LOCALE = { de, en: enUS, sk: de };
import { Loader2, ChevronLeft, ChevronRight, Calendar, Briefcase, Car, MapPin, Plane, Stethoscope } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "../../i18n/LanguageProvider";

const API = "/api";
const DAY_NAMES_BY_LANG = {
  de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
  en: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
  sk: ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"],
};

const hm = (m) => {
  const v = Math.abs(m || 0);
  return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`;
};
const fmtHM = (mins) => `${Math.floor((mins || 0) / 60)}h ${String((mins || 0) % 60).padStart(2, "0")}m`;

function getStatusInfo(diff, soll) {
  if (soll === 0) return { color: "#9CA3AF", bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.2)", labelKey: "msk.kein_soll", labelFb: "Kein Soll" };
  if (diff >= 0) return { color: "#4ADE80", bg: "rgba(74,222,128,0.1)", border: "rgba(74,222,128,0.2)", labelKey: "msk.im_plan", labelFb: "Im Plan" };
  const pct = soll > 0 ? (diff / soll) : 0;
  if (pct >= -0.15) return { color: "#FBBF24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.2)", labelKey: "msk.leicht_drunter", labelFb: "Leicht drunter" };
  return { color: "#F87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.2)", labelKey: "msk.hinterher", labelFb: "Hinterher" };
}

export default function MobileStundenkonto({
  userIdOverride: userIdOverrideProp = null,
  showEmployeeSelector: showSelectorProp = null,
  employeeList: employeeListProp = null,
  onEmployeeChange = null,
  containerClass = null,
} = {}) {
  const { t, lang } = useTranslation();
  const { user } = useAuth();
  const locale = DATE_LOCALE[lang] || de;
  const dayNames = DAY_NAMES_BY_LANG[lang] || DAY_NAMES_BY_LANG.de;

  // Auto-Detect: wenn showEmployeeSelector nicht explizit gesetzt → an Rolle knüpfen
  const role = (user?.role || "").toLowerCase();
  const isPrivileged = role === "admin" || role === "projektleiter"
    || !!user?.permissions?.can_view_all_entries;
  const showEmployeeSelector = showSelectorProp === null ? isPrivileged : !!showSelectorProp;

  // Wenn keine employeeList mitgegeben aber Selector aktiv → intern nachladen
  const [internalEmployees, setInternalEmployees] = useState([]);
  useEffect(() => {
    if (!showEmployeeSelector) return;
    if (employeeListProp !== null) return; // Wrapper liefert Liste
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${API}/users`);
        if (!r.ok) return;
        const list = await r.json();
        if (alive) {
          setInternalEmployees(
            (list || [])
              .filter(u => u.is_active !== false)
              .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""))
          );
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [showEmployeeSelector, employeeListProp]);
  const employeeList = employeeListProp !== null ? employeeListProp : internalEmployees;

  // Interner Zustand für Selector, falls kein Parent-Handler
  const [internalSelectedId, setInternalSelectedId] = useState(null);
  const userIdOverride = onEmployeeChange !== null ? userIdOverrideProp : internalSelectedId;
  const handleEmployeeChange = onEmployeeChange !== null
    ? onEmployeeChange
    : setInternalSelectedId;

  const [todayData, setTodayData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [monthCursor, setMonthCursor] = useState(() => new Date());
  const [weekCursor, setWeekCursor] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [loading, setLoading] = useState(true);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [selectedDayIso, setSelectedDayIso] = useState(null);
  const currentTargetName = userIdOverride
    ? (employeeList.find(u => u.id === userIdOverride)?.name || null)
    : null;

  const userQS = userIdOverride ? `?user_id=${encodeURIComponent(userIdOverride)}` : "";
  const userQSAmp = userIdOverride ? `&user_id=${encodeURIComponent(userIdOverride)}` : "";

  const loadStats = useCallback(async (anchor = null) => {
    setLoadingWeek(true);
    try {
      const anchorStr = anchor ? format(anchor, "yyyy-MM-dd") : null;
      const params = [];
      if (userIdOverride) params.push(`user_id=${encodeURIComponent(userIdOverride)}`);
      if (anchorStr) params.push(`week_anchor=${anchorStr}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const res = await authFetch(`${API}/timesheet/my-today${qs}`);
      if (res.ok) setTodayData(await res.json());
    } catch { /* noop */ }
    setLoadingWeek(false);
  }, [userIdOverride]);

  const loadMonth = useCallback(async (cursor) => {
    setLoadingMonth(true);
    try {
      const y = cursor.getFullYear();
      const m = cursor.getMonth() + 1;
      const res = await authFetch(`${API}/timesheet/my-month?year=${y}&month=${m}${userQSAmp}`);
      if (res.ok) setMonthData(await res.json());
    } catch {}
    setLoadingMonth(false);
  }, [userQSAmp]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadStats(weekCursor), loadMonth(monthCursor)]);
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdOverride]);

  // Wochenwechsel: nur Stats neu laden
  useEffect(() => {
    loadStats(weekCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekCursor]);

  useEffect(() => {
    if (loading) return;
    loadMonth(monthCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthCursor]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#0f1117]">
        <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    );
  }

  if (!todayData) {
    return (
      <div className="px-4 py-8 text-center text-gray-500 text-sm min-h-screen bg-[#0f1117]" data-testid="m-stundenkonto">
        {t("msk.no_data", "Keine Daten verfügbar")}
      </div>
    );
  }

  const { today: td, week: wk, is_nachunternehmer } = todayData;
  const todayStatus = getStatusInfo(td.diff_minutes, td.soll_minutes);
  const isAbsence = td.absence_type && td.absence_type !== "arbeitstag";
  const absLabel = td.absence_type === "urlaub" ? t("df.abs_urlaub", "Urlaub")
    : td.absence_type === "krank" ? t("df.abs_krank", "Krank")
    : td.absence_type === "feiertag" ? t("df.abs_feiertag", "Feiertag")
    : td.absence_type === "frei" ? t("df.abs_frei", "Frei") : null;

  const weekMax = Math.max(600, ...wk.days.map(d => Math.max(d.ist_minutes, d.soll_minutes)));
  const firstDay = wk.days[0]?.date ? parseISO(wk.days[0].date) : new Date();
  const kw = getISOWeek(firstDay);

  const monthTotalWork = monthData?.total_work_minutes ?? 0;
  const monthSoll = monthData?.total_soll_minutes ?? 0;
  const monthDiff = monthData?.diff_minutes ?? 0;
  const monthStatus = getStatusInfo(monthDiff, monthSoll);

  return (
    <>
    <motion.div
      className={containerClass || "px-4 py-4 space-y-4 min-h-screen bg-[#0f1117] text-white pb-24 w-full max-w-full overflow-x-hidden"}
      data-testid="m-stundenkonto"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* ADMIN: Mitarbeiter-Auswahl */}
      {showEmployeeSelector && (
        <div className="rounded-xl bg-white/[0.04] border border-white/8 p-3 flex items-center gap-2"
             data-testid="sk-employee-selector-wrap">
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider shrink-0">
            {t("msk_o.employee", "Mitarbeiter")}
          </span>
          <select
            value={userIdOverride || ""}
            onChange={(e) => handleEmployeeChange?.(e.target.value || null)}
            className="flex-1 bg-zinc-900 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            data-testid="sk-employee-selector"
          >
            <option value="">{t("msk_o.own_account", "Mein Konto")}</option>
            {employeeList.map(u => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}{u.role ? ` · ${u.role}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* HEUTE */}
      <div
        className="rounded-xl border-2 p-4"
        style={{ backgroundColor: todayStatus.bg, borderColor: todayStatus.border }}
        data-testid="sk-today-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("msk.heute", "Heute")}</div>
            <div className="text-[11px] text-gray-500">{format(new Date(), "EEEE, dd. MMMM", { locale })}</div>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ backgroundColor: todayStatus.color + "18" }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: todayStatus.color }} />
            <span className="text-[11px] font-semibold" style={{ color: todayStatus.color }}>
              {isAbsence ? absLabel : t(todayStatus.labelKey, todayStatus.labelFb)}
            </span>
          </div>
        </div>
        {isAbsence ? (
          <div className="text-center py-3">
            <span className="text-lg font-semibold" style={{ color: todayStatus.color }}>{absLabel}</span>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <StatBlock label={t("msk.gearbeitet", "Gearbeitet")} value={hm(td.ist_minutes)} />
            {!is_nachunternehmer && <StatBlock label={t("msk.soll", "Soll")} value={hm(td.soll_minutes)} muted />}
            {!is_nachunternehmer && <StatBlock label={t("msk.differenz", "Differenz")} value={`${td.diff_minutes >= 0 ? "+" : ""}${hm(td.diff_minutes)}`} color={todayStatus.color} />}
          </div>
        )}
      </div>

      {/* WOCHE */}
      <div className="bg-white/[0.04] rounded-xl border border-white/8 p-4" data-testid="sk-week-card">
        <div className="flex items-center justify-between mb-3 gap-2">
          <button
            onClick={() => setWeekCursor(c => subDays(c, 7))}
            data-testid="sk-week-prev"
            className="w-7 h-7 rounded-md hover:bg-white/[0.06] flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            title={t("msk_o.prev_week", "Vorherige Woche")}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 flex items-center justify-center gap-2">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t("msk_o.week", "Woche")}</div>
            <div className="text-[11px] font-semibold text-blue-300">KW {kw} · {format(firstDay, "dd. MMM", { locale })}</div>
            {format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd") !== format(weekCursor, "yyyy-MM-dd") && (
              <button
                onClick={() => setWeekCursor(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                data-testid="sk-week-today"
                className="text-[10px] px-1.5 py-0.5 rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 transition-colors"
                title="Zurück zu dieser Woche"
              >
                Heute
              </button>
            )}
          </div>
          <button
            onClick={() => setWeekCursor(c => addDays(c, 7))}
            data-testid="sk-week-next"
            className="w-7 h-7 rounded-md hover:bg-white/[0.06] flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            title={t("msk_o.next_week", "Nächste Woche")}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-1.5">
          {wk.days.map((d, i) => {
            const pctIst = weekMax > 0 ? (d.ist_minutes / weekMax) * 100 : 0;
            const isToday = d.date === format(new Date(), "yyyy-MM-dd");
            const isAbs = d.absence_type && d.absence_type !== "arbeitstag";
            const diff = d.ist_minutes - d.soll_minutes;
            const hasData = d.ist_minutes > 0 || isAbs;
            const absLabelInline = isAbs ? {
              urlaub: t("df.abs_urlaub", "Urlaub"),
              krank: t("df.abs_krank", "Krank"),
              feiertag: t("df.abs_feiertag", "Feiertag"),
              frei: t("df.abs_frei", "Frei"),
            }[d.absence_type] : null;
            const barColor = d.is_weekend ? "#4B5563" : isAbs ? (d.absence_type === "urlaub" ? "#FBBF24" : d.absence_type === "krank" ? "#F87171" : "#60A5FA") : diff >= 0 ? "#4ADE80" : d.ist_minutes > 0 ? "#FBBF24" : "#374151";
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => setSelectedDayIso(d.date)}
                data-testid={`sk-week-day-${d.date}`}
                className={`w-full text-left flex items-center gap-2.5 px-1 py-0.5 rounded-md transition-colors hover:bg-white/[0.04] cursor-pointer ${isToday ? "bg-blue-500/[0.06]" : ""}`}
              >
                <span className={`w-7 text-[12px] font-semibold ${isToday ? "text-blue-300" : d.is_weekend ? "text-gray-600" : "text-gray-400"}`}>{dayNames[i]}</span>
                <div className="flex-1 relative h-5 rounded-md overflow-hidden bg-white/[0.06]">
                  <div className="absolute left-0 top-0 h-full rounded-md transition-all duration-300" style={{ width: `${Math.max(pctIst, hasData ? 4 : 0)}%`, backgroundColor: barColor }} />
                  {isAbs && (<span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow">{absLabelInline}</span>)}
                </div>
                <span className={`w-12 text-right text-[12px] font-mono ${d.ist_minutes > 0 ? "text-gray-300 font-medium" : "text-gray-600"}`}>{d.ist_minutes > 0 ? hm(d.ist_minutes) : "---"}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
          <span className="text-[11px] font-semibold text-gray-500">{t("msk_o.gesamt", "Gesamt")}</span>
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-bold text-white font-mono">{hm(wk.ist_minutes)}</span>
            {!is_nachunternehmer && (
              <>
                <span className="text-[11px] text-gray-500">/ {hm(wk.soll_minutes)}</span>
                <span className={`text-[12px] font-bold font-mono ${wk.diff_minutes >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {wk.diff_minutes >= 0 ? "+" : ""}{hm(wk.diff_minutes)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MONAT */}
      <div
        className="rounded-xl border-2 p-4"
        style={{ backgroundColor: monthStatus.bg, borderColor: monthStatus.border }}
        data-testid="sk-month-card"
      >
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setMonthCursor(d => subMonths(d, 1))}
            className="p-1.5 rounded-md hover:bg-white/[0.06] transition-colors"
            data-testid="sk-month-prev"
            aria-label={t("msk_o.prev_month", "Vorheriger Monat")}
          >
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          </button>
          <div className="text-center">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">{t("msk_o.month", "Monat")}</div>
            <div className="text-sm font-semibold text-white flex items-center gap-1.5" data-testid="sk-month-label">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
              {monthData?.month_name || format(monthCursor, "MMMM", { locale })} {monthData?.year || monthCursor.getFullYear()}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMonthCursor(d => addMonths(d, 1))}
            className="p-1.5 rounded-md hover:bg-white/[0.06] transition-colors"
            data-testid="sk-month-next"
            aria-label={t("msk_o.next_month", "Nächster Monat")}
          >
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        {loadingMonth ? (
          <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-gray-500" /></div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <StatBlock label={t("msk.gearbeitet", "Gearbeitet")} value={fmtHM(monthTotalWork)} />
              {!is_nachunternehmer && <StatBlock label={t("msk.soll", "Soll")} value={fmtHM(monthSoll)} muted />}
              {!is_nachunternehmer && <StatBlock label={t("msk.differenz", "Differenz")} value={`${monthDiff >= 0 ? "+" : ""}${fmtHM(monthDiff)}`} color={monthStatus.color} />}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <InfoChip icon={Briefcase} label={t("msk_o.workdays", "Arbeitstage")} value={monthData?.worked_days ?? 0} />
              <InfoChip icon={Plane} label={t("df.abs_urlaub", "Urlaub")} value={monthData?.vacation_days ?? 0} />
              <InfoChip icon={Stethoscope} label={t("df.abs_krank", "Krank")} value={monthData?.sick_days ?? 0} />
              <InfoChip icon={Calendar} label={t("msk_o.holidays", "Feiertage")} value={monthData?.holiday_days ?? 0} />
              <InfoChip icon={Car} label={t("msk_o.travel_time", "Fahrzeit")} value={fmtHM(monthData?.travel_minutes ?? 0)} />
              <InfoChip icon={MapPin} label={t("msk_o.kilometers", "Kilometer")} value={`${(monthData?.travel_km ?? 0).toFixed(0)} km`} />
            </div>
          </>
        )}
      </div>

      {/* PROJEKT-AUFSCHLÜSSELUNG */}
      <div className="bg-white/[0.04] rounded-xl border border-white/8 p-4" data-testid="sk-projects-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t("msk_o.projects", "Projekte")} ({monthData?.month_name})</div>
          <div className="text-[11px] text-gray-500">{(monthData?.project_breakdown || []).length} {t("msk_o.active", "aktiv")}</div>
        </div>
        {!monthData?.project_breakdown || monthData.project_breakdown.length === 0 ? (
          <div className="py-6 text-center text-[12px] text-gray-500">{t("msk_o.no_project_entries", "Keine Projekteinträge in diesem Monat")}</div>
        ) : (
          <div className="space-y-2">
            {monthData.project_breakdown.map((p, idx) => {
              const max = monthData.project_breakdown[0]?.minutes || 1;
              const pct = max > 0 ? (p.minutes / max) * 100 : 0;
              return (
                <div key={p.project_id || idx} className="space-y-1" data-testid={`sk-project-${idx}`}>
                  <div className="flex items-center gap-2">
                    {p.short_code && (<span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-blue-500/20 text-blue-300 shrink-0">{p.short_code}</span>)}
                    <span className="text-[12px] text-gray-100 flex-1 truncate">{p.name}</span>
                    <span className="text-[12px] font-mono font-bold text-white">{fmtHM(p.minutes)}</span>
                  </div>
                  <div className="relative h-2 rounded-full overflow-hidden bg-white/[0.06]">
                    <div className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-gray-500">{p.days} {p.days === 1 ? "Tag" : "Tage"}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
    {selectedDayIso && (
      <DayEntriesSheet
        isoDate={selectedDayIso}
        userId={userIdOverride}
        userName={currentTargetName}
        onClose={() => {
          setSelectedDayIso(null);
          loadStats(weekCursor);
          loadMonth(monthCursor);
        }}
      />
    )}
    </>
  );
}

function StatBlock({ label, value, muted, color }) {
  return (
    <div className="text-center min-w-0">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl sm:text-2xl font-bold font-mono tabular-nums" style={color ? { color } : undefined}>
        <span className={muted ? "text-gray-400" : color ? "" : "text-white"}>{value}</span>
      </div>
    </div>
  );
}

function InfoChip({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/8">
      <Icon className="w-3 h-3 text-gray-500 shrink-0" />
      <span className="text-gray-500">{label}</span>
      <span className="ml-auto text-gray-200 font-mono font-semibold">{value}</span>
    </div>
  );
}
